import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, CheckCircle, ArrowRight, Bell, TrendingUp, Shield, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import SEO from '../components/SEO';

const CloudOpsSolutions = () => {
  const services = [
    {
      icon: Bell,
      title: 'Proactive Monitoring & Alerting',
      description: 'Real-time monitoring with intelligent alerting systems to catch issues before they impact users'
    },
    {
      icon: TrendingUp,
      title: 'SLA-Backed Uptime Guarantees',
      description: '99.9% uptime guarantee with comprehensive service level agreements'
    },
    {
      icon: RefreshCw,
      title: 'Automated Incident Response',
      description: 'Self-healing systems with automated incident response and recovery mechanisms'
    },
    {
      icon: Activity,
      title: 'Post-Incident Analysis',
      description: 'Detailed post-mortem analysis with feedback loops for continuous improvement'
    }
  ];

  const benefits = [
    '99.98% uptime achieved',
    '24/7 monitoring and support',
    'Automated incident response',
    'Real-time system health insights',
    'Rapid incident resolution',
    'Continuous optimization'
  ];

  const metrics = [
    { value: '99.98%', label: 'Average Uptime' },
    { value: '< 5 min', label: 'Response Time' },
    { value: '24/7', label: 'Support Coverage' }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="CloudOps RaaS — Reliability as a Service & 24/7 Monitoring | NIVA Digital AI"
        description="CloudOps Reliability as a Service with 24/7 monitoring, incident management, performance optimization, and proactive maintenance."
        keywords="CloudOps RaaS, reliability as a service, 24/7 cloud monitoring, incident management, cloud performance optimization"
        canonical={`${process.env.REACT_APP_BASE_URL || 'https://nivadigital.io'}/services/cloudops`}
      />
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-purple-100 text-purple-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Activity size={20} />
                <span>CloudOps RaaS</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Reliability as a
                <span className="block text-red-600 mt-2">Service</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Gain real-time insights into system health with proactive monitoring and rapid incident resolution. CloudOps RaaS embeds reliability engineering as an always-on service layer.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                  Get Started <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-purple-100 to-indigo-50 rounded-2xl p-8 shadow-2xl">
                <Activity className="text-purple-600 mx-auto" size={200} strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              CloudOps RaaS Features
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive reliability services beyond typical managed services
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="border-2 hover:border-red-600 transition-all duration-300 hover:shadow-xl">
                  <CardHeader>
                    <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="text-red-600" size={28} />
                    </div>
                    <CardTitle className="text-2xl mb-2">{service.title}</CardTitle>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Performance Metrics */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Performance Metrics
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Industry-leading reliability and response times
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {metrics.map((metric, index) => (
              <Card key={index} className="border-2 border-red-600 text-center">
                <CardContent className="pt-8">
                  <p className="text-5xl font-bold text-red-600 mb-2">{metric.value}</p>
                  <p className="text-gray-600 text-lg">{metric.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-2 hover:border-red-600 transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                    <p className="text-gray-700 font-medium">{benefit}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How CloudOps RaaS Works
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2">
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-red-600">1</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Monitor</h3>
                <p className="text-gray-600">Continuous monitoring of all critical systems with intelligent alerting</p>
              </CardContent>
            </Card>
            <Card className="border-2">
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-red-600">2</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Respond</h3>
                <p className="text-gray-600">Automated incident response with self-healing capabilities</p>
              </CardContent>
            </Card>
            <Card className="border-2">
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-red-600">3</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Improve</h3>
                <p className="text-gray-600">Post-incident analysis and continuous improvement feedback loops</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pod-Based Support */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Pod-Based CloudOps Teams
              </h2>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                Deploy pod-based CloudOps teams providing monitoring, incident response, and SLA-backed support.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Dedicated Teams</h3>
                    <p className="text-gray-600">CloudOps specialists, SREs, and infrastructure architects</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Weekly Reviews</h3>
                    <p className="text-gray-600">Monthly reporting and custom dashboards for full transparency</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Resource Optimization</h3>
                    <p className="text-gray-600">Cost governance ensuring you only pay for what you need</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-purple-100 to-indigo-50 rounded-2xl p-12 shadow-2xl">
                <div className="space-y-6">
                  <div className="bg-white p-6 rounded-lg shadow">
                    <Activity className="text-purple-600 mb-2" size={32} />
                    <h4 className="font-semibold">Always-On Service</h4>
                    <p className="text-sm text-gray-600 mt-1">24/7 reliability engineering</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow">
                    <Shield className="text-purple-600 mb-2" size={32} />
                    <h4 className="font-semibold">SLA Backed</h4>
                    <p className="text-sm text-gray-600 mt-1">Guaranteed uptime commitments</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-red-600 via-red-500 to-orange-600 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ensure Maximum Uptime
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Get started with CloudOps RaaS and achieve 99.9%+ uptime
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Contact Us <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CloudOpsSolutions;